import {
  DEFAULT_WHEEL_TICK_SOUND_PRESET_ID,
  isWheelTickSoundPreset,
  isWheelTickSoundBackground,
} from '../wheel/wheelSoundPresets';
import { WHEEL_THEME_POINTER_URLS } from '../wheel/wheelThemes';

export const defaultWheelAppearance = {
  version: 9,
  wheel: {
    background: '#111827',
    radius: null,
    debug: false,
  },
  sectors: {
    colorMode: 'original',
    palette: ['#7c3aed', '#db2777', '#ea580c', '#ca8a04', '#16a34a', '#0891b2'],
  },
  text: {
    color: '#ffffff',
    fontFamily: 'Roboto Mono',
    fontSize: 3.4,
    fontWeight: 700,
    radius: 0.66,
    innerRadius: 0.13,
    offsetX: 0,
    offsetY: 0,
    orientation: 'radial',
    align: 'middle',
    overflow: 'shrink-wrap',
    minFontSize: 1.9,
    maxLines: 2,
    strokeColor: '#000000',
    strokeWidth: 0.2,
    shadow: {
      color: 'transparent',
      blur: 0,
      offsetX: 0,
      offsetY: 0,
    },
  },
  border: {
    color: '#ffffff',
    width: 0.4,
    shadow: {
      color: 'transparent',
      blur: 0,
      offsetX: 0,
      offsetY: 0,
    },
  },
  dividers: {
    color: '#ffffff',
    width: 0.2,
    shadow: {
      color: 'transparent',
      blur: 0,
      offsetX: 0,
      offsetY: 0,
    },
  },
  overlay: {
    source: 'custom',
    opacity: 1,
    scale: 1,
  },
  center: {
    source: 'emote',
    showEmote: true,
    size: 13,
    opacity: 1,
    scale: 1,
    borderRadius: 50,
    background: 'transparent',
    borderColor: 'transparent',
    borderWidth: 0,
  },
  pointer: {
    source: 'default',
    position: 'right',
    size: 13,
    offset: -3,
    opacity: 1,
    color: '#f8fafc',
    scale: 1,
  },
  idleAnimation: {
    enabled: false,
    duration: 2800,
    rotation: 0.65,
    scale: 1,
  },
  sounds: {
    tick: {
      source: 'preset',
      presetId: DEFAULT_WHEEL_TICK_SOUND_PRESET_ID,
      isBackground: false,
    },
  },
  sectorOverrides: {},
  media: {
    overlay: null,
    center: null,
    pointer: null,
    sectors: {},
    sounds: {
      start: null,
      tick: null,
      win: null,
    },
  },
};

const VIDEO_URL_PATTERN = /\.(mp4|m4v|mov|ogv|webm)(?:[?#].*)?$/i;
const AUDIO_URL_PATTERN = /\.(aac|flac|m4a|mp3|oga|ogg|wav)(?:[?#].*)?$/i;

const isObject = (value) => value && typeof value === 'object' && !Array.isArray(value);

const getCdnThemeAssetUrl = (theme, fileName) => (
  `${process.env.REACT_APP_CDN_BASE_URL}/auction/themes/${theme}/${fileName}`
);

const CDN_THEME_ASSET_URLS = {
  'auction/neon-center.svg': getCdnThemeAssetUrl('neon', 'neon-center.svg'),
  'auction/neon-center.webm': getCdnThemeAssetUrl('neon', 'neon-center.webm'),
  'auction/neon-frame.svg': getCdnThemeAssetUrl('neon', 'neon-frame.svg'),
  'auction/neon-frame.webm': getCdnThemeAssetUrl('neon', 'neon-frame.webm'),
  'auction/neon-pointer.svg': WHEEL_THEME_POINTER_URLS.neon,
  'auction/realistic-space-center.svg': getCdnThemeAssetUrl('space', 'realistic-space-center.svg'),
  'auction/realistic-space-center.webm': getCdnThemeAssetUrl('space', 'realistic-space-center.webm'),
  'auction/realistic-space-frame.svg': getCdnThemeAssetUrl('space', 'realistic-space-frame.svg'),
  'auction/realistic-space-frame.webm': getCdnThemeAssetUrl('space', 'realistic-space-frame.webm'),
  'auction/realistic-space-pointer.svg': WHEEL_THEME_POINTER_URLS.realisticSpace,
  'themes/animated-neon/neon-center.svg': getCdnThemeAssetUrl('neon', 'neon-center.svg'),
  'themes/animated-neon/neon-center.webm': getCdnThemeAssetUrl('neon', 'neon-center.webm'),
  'themes/animated-neon/neon-frame.svg': getCdnThemeAssetUrl('neon', 'neon-frame.svg'),
  'themes/animated-neon/neon-frame.webm': getCdnThemeAssetUrl('neon', 'neon-frame.webm'),
  'themes/animated-neon/neon-pointer.svg': WHEEL_THEME_POINTER_URLS.neon,
  'themes/animated-realistic-space/realistic-space-center.svg': getCdnThemeAssetUrl('space', 'realistic-space-center.svg'),
  'themes/animated-realistic-space/realistic-space-center.webm': getCdnThemeAssetUrl('space', 'realistic-space-center.webm'),
  'themes/animated-realistic-space/realistic-space-frame.svg': getCdnThemeAssetUrl('space', 'realistic-space-frame.svg'),
  'themes/animated-realistic-space/realistic-space-frame.webm': getCdnThemeAssetUrl('space', 'realistic-space-frame.webm'),
  'themes/animated-realistic-space/realistic-space-pointer.svg': WHEEL_THEME_POINTER_URLS.realisticSpace,
};

const normalizeLocalThemeAsset = (asset) => {
  if (!isObject(asset) || typeof asset.url !== 'string') {
    return asset;
  }

  const assetUrl = asset.url.trim();
  const match = assetUrl.match(/(?:^|\/)((?:auction\/(?:neon|realistic-space)-|themes\/(?:animated-neon|animated-realistic-space)\/)[^?#]+)(?:[?#].*)?$/i);
  const cdnUrl = match && CDN_THEME_ASSET_URLS[match[1].toLowerCase()];

  if (cdnUrl) {
    return {
      ...asset,
      name: cdnUrl,
      url: cdnUrl,
    };
  }

  if (!match || assetUrl.startsWith('/themes/')) {
    return asset;
  }

  const url = `/${match[1]}`;
  return {
    ...asset,
    name: url,
    url,
  };
};

const mergeAppearance = (defaults, saved) => {
  if (!isObject(saved)) {
    return defaults;
  }

  return Object.keys(defaults).reduce((result, key) => {
    const defaultValue = defaults[key];
    const savedValue = saved[key];

    if (isObject(defaultValue)) {
      result[key] = mergeAppearance(defaultValue, savedValue);
    } else {
      result[key] = savedValue === undefined ? defaultValue : savedValue;
    }
    return result;
  }, {});
};

export const normalizeWheelAppearance = (savedAppearance) => {
  const merged = mergeAppearance(defaultWheelAppearance, savedAppearance);
  const savedPalette = savedAppearance?.sectors?.palette;
  const savedOverrides = savedAppearance?.sectorOverrides;
  const savedSectorMedia = savedAppearance?.media?.sectors;
  const savedSoundMedia = savedAppearance?.media?.sounds;
  const legacySpinAsset = savedSoundMedia?.spin;
  const isLegacyRealisticSpaceBeep = typeof legacySpinAsset?.url === 'string'
    && /\/assets\/realistic-space\/beep\.mp3(?:[?#].*)?$/i.test(legacySpinAsset.url);
  const legacyTickAsset = savedSoundMedia?.tick
    || (isLegacyRealisticSpaceBeep ? null : legacySpinAsset)
    || null;
  const savedTickSound = savedAppearance?.sounds?.tick;
  const tickSource = ['preset', 'custom', 'none'].includes(savedTickSound?.source)
    ? savedTickSound.source
    : isLegacyRealisticSpaceBeep
      ? 'preset'
      : legacyTickAsset
        ? 'custom'
        : defaultWheelAppearance.sounds.tick.source;
  const tickPresetId = isWheelTickSoundPreset(savedTickSound?.presetId)
    ? savedTickSound.presetId
    : isLegacyRealisticSpaceBeep
      ? 'beep'
      : defaultWheelAppearance.sounds.tick.presetId;
  const tickIsBackground = isWheelTickSoundBackground({
    source: tickSource,
    presetId: tickPresetId,
    isBackground: savedTickSound?.isBackground,
  });
  const savedOverlaySource = savedAppearance?.overlay?.source;
  const overlaySource = savedOverlaySource === 'theme'
    ? 'custom'
    : ['custom', 'none'].includes(savedOverlaySource)
      ? savedOverlaySource
      : defaultWheelAppearance.overlay.source;
  const savedCenterSource = savedAppearance?.center?.source;
  const centerSource = savedCenterSource === 'theme'
    ? (savedAppearance?.media?.center ? 'custom' : 'emote')
    : ['emote', 'custom', 'none'].includes(savedCenterSource)
      ? savedCenterSource
      : defaultWheelAppearance.center.source;
  const savedPointerSource = savedAppearance?.pointer?.source;
  const pointerSource = savedPointerSource === 'theme'
    ? 'default'
    : ['default', 'custom', 'none'].includes(savedPointerSource)
      ? savedPointerSource
      : defaultWheelAppearance.pointer.source;

  return {
    ...merged,
    version: defaultWheelAppearance.version,
    sectors: {
      ...merged.sectors,
      palette: Array.isArray(savedPalette) && savedPalette.length > 0
        ? savedPalette
        : defaultWheelAppearance.sectors.palette,
    },
    sounds: {
      ...merged.sounds,
      tick: {
        source: tickSource,
        presetId: tickPresetId,
        isBackground: tickIsBackground,
      },
    },
    overlay: {
      ...merged.overlay,
      source: overlaySource,
    },
    center: {
      ...merged.center,
      source: centerSource,
    },
    pointer: {
      ...merged.pointer,
      source: pointerSource,
    },
    sectorOverrides: isObject(savedOverrides) ? savedOverrides : {},
    media: {
      ...merged.media,
      overlay: normalizeLocalThemeAsset(merged.media.overlay),
      center: normalizeLocalThemeAsset(merged.media.center),
      pointer: normalizeLocalThemeAsset(merged.media.pointer),
      sectors: isObject(savedSectorMedia) ? savedSectorMedia : {},
      sounds: isObject(savedSoundMedia)
        ? {
          ...defaultWheelAppearance.media.sounds,
          start: savedSoundMedia.start || null,
          tick: legacyTickAsset,
          win: savedSoundMedia.win || null,
        }
        : defaultWheelAppearance.media.sounds,
    },
  };
};

export const setAppearanceValue = (appearance, path, value) => {
  const [key, ...remainingPath] = path;
  if (!key) {
    return appearance;
  }
  if (remainingPath.length === 0) {
    return { ...appearance, [key]: value };
  }

  return {
    ...appearance,
    [key]: setAppearanceValue(appearance[key] || {}, remainingPath, value),
  };
};

export const createStoredMedia = (file) => file ? {
  name: file.name,
  type: file.type,
  size: file.size,
  lastModified: file.lastModified,
  blob: file,
} : null;

export const createStoredMediaUrl = (url, fallbackKind = 'image') => {
  const normalizedUrl = typeof url === 'string' ? url.trim() : '';

  if (!normalizedUrl) {
    return null;
  }

  const kind = VIDEO_URL_PATTERN.test(normalizedUrl)
    ? 'video'
    : AUDIO_URL_PATTERN.test(normalizedUrl)
      ? 'audio'
      : fallbackKind;

  return {
    name: normalizedUrl,
    type: `${kind}/external`,
    url: normalizedUrl,
    kind,
  };
};

export const normalizeWheelAppearanceThemeName = (name) => (
  typeof name === 'string' ? name.trim().slice(0, 80) : ''
);

const PORTABLE_THEME_FORMAT = 'whatyouwish-wheel-theme';
const PORTABLE_THEME_VERSION = 1;
const MAX_PORTABLE_THEME_LENGTH = 500000;

const createPortableMediaAsset = (asset, fallbackKind = 'image') => {
  if (!isObject(asset) || typeof asset.url !== 'string') {
    return null;
  }

  return createStoredMediaUrl(asset.url, fallbackKind);
};

const createPortableAppearance = (appearance) => {
  const normalizedAppearance = normalizeWheelAppearance(appearance);
  const sectorMedia = Object.entries(normalizedAppearance.media.sectors || {})
    .reduce((result, [sectorId, asset]) => {
      const portableAsset = createPortableMediaAsset(asset);
      if (portableAsset) {
        result[sectorId] = portableAsset;
      }
      return result;
    }, {});
  const soundMedia = Object.entries(normalizedAppearance.media.sounds || {})
    .reduce((result, [slot, asset]) => {
      result[slot] = createPortableMediaAsset(asset, 'audio');
      return result;
    }, {});

  return {
    ...normalizedAppearance,
    media: {
      overlay: createPortableMediaAsset(normalizedAppearance.media.overlay),
      center: createPortableMediaAsset(normalizedAppearance.media.center),
      pointer: createPortableMediaAsset(normalizedAppearance.media.pointer),
      sectors: sectorMedia,
      sounds: {
        ...defaultWheelAppearance.media.sounds,
        ...soundMedia,
      },
    },
  };
};

const createPortableWheel = (wheel) => {
  if (
    !isObject(wheel)
    || !Number.isFinite(wheel.radius)
    || wheel.radius <= 0
    || wheel.radius > 1
    || !isObject(wheel.theme)
  ) {
    return null;
  }

  return {
    radius: wheel.radius,
    overlayImage: typeof wheel.overlayImage === 'string' ? wheel.overlayImage : undefined,
    theme: {
      text: isObject(wheel.theme.text) ? { ...wheel.theme.text } : {},
      border: isObject(wheel.theme.border) ? { ...wheel.theme.border } : {},
      dividers: isObject(wheel.theme.dividers) ? { ...wheel.theme.dividers } : {},
    },
  };
};

export const createWheelAppearanceThemeCode = ({ name, wheel, appearance }) => {
  const portableWheel = createPortableWheel(wheel);
  const normalizedName = normalizeWheelAppearanceThemeName(name);

  if (!normalizedName || !portableWheel) {
    return '';
  }

  const code = JSON.stringify({
    format: PORTABLE_THEME_FORMAT,
    version: PORTABLE_THEME_VERSION,
    name: normalizedName,
    wheel: portableWheel,
    appearance: createPortableAppearance(appearance),
  });

  return code.length <= MAX_PORTABLE_THEME_LENGTH ? code : '';
};

export const parseWheelAppearanceThemeCode = (code) => {
  if (typeof code !== 'string' || !code.trim() || code.length > MAX_PORTABLE_THEME_LENGTH) {
    return null;
  }

  try {
    const value = JSON.parse(code);
    const portableWheel = createPortableWheel(value?.wheel);
    const normalizedName = normalizeWheelAppearanceThemeName(value?.name);

    if (
      value?.format !== PORTABLE_THEME_FORMAT
      || value?.version !== PORTABLE_THEME_VERSION
      || !normalizedName
      || !portableWheel
      || !isObject(value?.appearance)
    ) {
      return null;
    }

    return {
      name: normalizedName,
      wheel: portableWheel,
      appearance: createPortableAppearance(value.appearance),
    };
  } catch {
    return null;
  }
};

const isBlob = (value) => typeof Blob !== 'undefined' && value instanceof Blob;

export const isWheelAppearanceEqual = (first, second) => {
  if (Object.is(first, second)) {
    return true;
  }

  if (isBlob(first) || isBlob(second)) {
    return false;
  }

  if (!first || !second || typeof first !== 'object' || typeof second !== 'object') {
    return false;
  }

  const firstKeys = Object.keys(first);
  const secondKeys = Object.keys(second);
  if (firstKeys.length !== secondKeys.length) {
    return false;
  }

  return firstKeys.every((key) => (
    Object.prototype.hasOwnProperty.call(second, key)
      && isWheelAppearanceEqual(first[key], second[key])
  ));
};

export const createWheelAppearanceTheme = (name, appearance, wheel, id, updatedAt = Date.now()) => {
  const normalizedName = normalizeWheelAppearanceThemeName(name);

  if (!normalizedName || !id || !wheel) {
    return null;
  }

  return {
    id,
    name: normalizedName,
    type: 'custom',
    wheel,
    appearance: normalizeWheelAppearance(appearance),
    createdAt: updatedAt,
    updatedAt,
  };
};

export const normalizeWheelAppearanceTheme = (theme, fallbackWheel) => {
  if (!theme?.id || !fallbackWheel) {
    return null;
  }

  const wheel = theme.wheel || fallbackWheel;
  const savedAppearance = theme.appearance || theme.value;
  const normalizedAppearance = normalizeWheelAppearance(savedAppearance);
  const legacyOverlayUrl = savedAppearance?.overlay?.source === 'theme'
    && !savedAppearance?.media?.overlay
    && (wheel.overlayImage || fallbackWheel.overlayImage);
  const appearance = legacyOverlayUrl
    ? {
      ...normalizedAppearance,
      overlay: { ...normalizedAppearance.overlay, source: 'custom' },
      media: {
        ...normalizedAppearance.media,
        overlay: createStoredMediaUrl(legacyOverlayUrl),
      },
    }
    : normalizedAppearance;

  return {
    id: theme.id,
    name: normalizeWheelAppearanceThemeName(theme.name) || 'Custom theme',
    type: 'custom',
    wheel,
    appearance,
    createdAt: theme.createdAt || theme.updatedAt || Date.now(),
    updatedAt: theme.updatedAt || theme.createdAt || Date.now(),
  };
};
